import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ThemeToggleButton } from '../components/ui/ThemeToggleButton';
import { ThemeAwareLogo } from '../components/ui/ThemeAwareLogo';
import { apiService } from '../services/apiService';
import { SearchParameterType } from '../types';

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string;
  price: number;
  intCoins: number;
  durationDays: number;
  intCoinsExpirationDays: number;
  features: string[];
  status: 'active' | 'inactive';
}

export const PublicLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, logout } = useAuth();
  const [parameterTypes, setParameterTypes] = useState<SearchParameterType[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Interactive search simulation demo state
  const [demoType, setDemoType] = useState<'phone' | 'email' | 'domain' | 'ip'>('phone');
  const [demoQuery, setDemoQuery] = useState('+1 (555) 382-9102');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const [stats, setStats] = useState({
    totalSearches: 125000,
    activeUsers: 2850,
    dataSources: 45,
    responseTime: '< 1.8s'
  });

  useEffect(() => {
    loadPublicData();
  }, []);

  const loadPublicData = async () => {
    try {
      setIsLoading(true);
      
      const [types, plans] = await Promise.all([
        apiService.getParameterTypes().catch(err => {
          console.warn('Failed to load parameter types:', err);
          return [];
        }),
        apiService.getPublicSubscriptionPlans().catch(err => {
          console.warn('Failed to load subscription plans:', err);
          return [];
        })
      ]);
      
      setParameterTypes(types.slice(0, 8));
      
      const activePlans = plans
        .filter((plan: any) => plan.status === 'active')
        .sort((a: any, b: any) => a.price - b.price)
        .slice(0, 3);
      
      setSubscriptionPlans(activePlans);
    } catch (error) {
      console.error('Error loading public data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetStarted = () => {
    if (user) {
      navigate('/app/dashboard');
    } else {
      navigate('/register');
    }
  };

  const sampleQueries = {
    phone: '+1 (555) 382-9102',
    email: 'investigator@security-lab.io',
    domain: 'target-corp.com',
    ip: '198.51.100.42'
  };

  const handleDemoTypeChange = (type: 'phone' | 'email' | 'domain' | 'ip') => {
    setDemoType(type);
    setDemoQuery(sampleQueries[type]);
  };

  const faqs = [
    {
      q: 'What is IntFinder and what can it uncover?',
      a: 'IntFinder is an enterprise-grade OSINT (Open Source Intelligence) investigation platform that queries over 45+ specialized intelligence APIs and databases concurrently. You can investigate phone numbers, emails, domain names, IP addresses, usernames, and corporate records in real time.'
    },
    {
      q: 'Do you store or track the searches I perform?',
      a: 'Privacy and security are our highest priorities. IntFinder operates on a strict zero-query retention principle for public search records, and all data transmissions are secured with 256-bit TLS encryption.'
    },
    {
      q: 'How does the Int Coins credit model work?',
      a: 'IntFinder uses a transparent pay-as-you-go credit system. Each search consumes a set amount of Int Coins depending on the depth and sources queried. There are no surprise monthly subscription lock-ins.'
    },
    {
      q: 'Can law enforcement and authorized corporate teams use IntFinder?',
      a: 'Yes. We provide certified verification workflows for authorized law enforcement personnel, cybersecurity teams, and certified investigative agencies with enhanced audit tools.'
    }
  ];

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900 text-white">
        <LoadingSpinner text="Loading IntFinder platform..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 text-gray-900 dark:text-gray-100 selection:bg-indigo-500 selection:text-white">
      {/* Navigation */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/80 dark:border-gray-800 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            {/* Brand Logo */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3 group">
                <ThemeAwareLogo size="md" showText={false} />
                <span className="text-xl sm:text-2xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-600 via-indigo-500 to-cyan-500 bg-clip-text text-transparent group-hover:opacity-90 transition-opacity">
                  IntFinder
                </span>
              </Link>
              <span className="hidden sm:inline-flex items-center px-2 py-0.5 text-[11px] font-semibold tracking-wider uppercase rounded-full bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                v2.5 Pro
              </span>
            </div>

            {/* Desktop Navigation Links */}
            <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-gray-600 dark:text-gray-300">
              <a href="#capabilities" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                Capabilities
              </a>
              <a href="#how-it-works" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                How It Works
              </a>
              <a href="#pricing" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                Pricing
              </a>
              <a href="#security" className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                Security & Trust
              </a>
            </nav>

            {/* Action Buttons */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <ThemeToggleButton />
              
              {user ? (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/app/dashboard"
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm shadow-indigo-500/25 transition-all"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      navigate('/');
                    }}
                    className="hidden sm:inline-block text-sm text-gray-600 dark:text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 transition-colors font-medium"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <div className="hidden sm:flex items-center space-x-3">
                  <Link
                    to="/login"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 px-3 py-2 transition-colors"
                  >
                    Sign In
                  </Link>
                  <Button
                    onClick={handleGetStarted}
                    variant="primary"
                    size="sm"
                    className="shadow-sm shadow-indigo-500/30"
                  >
                    Get Started Free
                  </Button>
                </div>
              )}

              {/* Mobile menu button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(prev => !prev)}
                className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none"
                aria-label="Toggle Navigation"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg px-4 pt-3 pb-6 space-y-3">
            <a
              href="#capabilities"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-medium text-gray-700 dark:text-gray-300"
            >
              Capabilities
            </a>
            <a
              href="#how-it-works"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-medium text-gray-700 dark:text-gray-300"
            >
              How It Works
            </a>
            <a
              href="#pricing"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-medium text-gray-700 dark:text-gray-300"
            >
              Pricing
            </a>
            <a
              href="#security"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 text-base font-medium text-gray-700 dark:text-gray-300"
            >
              Security
            </a>
            <div className="pt-3 border-t border-gray-200 dark:border-gray-800 flex flex-col gap-2">
              {!user ? (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="w-full text-center py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 font-medium text-gray-800 dark:text-gray-200"
                  >
                    Sign In
                  </Link>
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleGetStarted();
                    }}
                    variant="primary"
                    className="w-full"
                  >
                    Get Started Free
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/app/dashboard');
                  }}
                  variant="primary"
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section with 3D Visual Rendering */}
      <section className="relative overflow-hidden pt-10 pb-20 lg:pt-16 lg:pb-32">
        {/* Ambient Glowing Blobs */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-indigo-500/20 via-purple-500/15 to-cyan-500/20 blur-[130px] rounded-full pointer-events-none -z-10" />
        <div className="absolute top-10 right-0 w-80 h-80 bg-cyan-400/15 blur-[100px] rounded-full pointer-events-none -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Copy & Interactive Demo */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-800/60 mb-6 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-ping" />
                <span>Next-Gen OSINT Intelligence Platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-[1.15] mb-6">
                Uncover The Invisible With{' '}
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-500 bg-clip-text text-transparent">
                  Unified OSINT Intelligence
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                Connect and query <strong>45+ global cybersecurity APIs and OSINT databases</strong> in seconds. 
                Investigate phone numbers, emails, domains, IP infrastructure, and threat vectors from a single unified workspace.
              </p>

              {/* Interactive Search Simulator */}
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl p-4 sm:p-5 border border-gray-200/80 dark:border-gray-700 shadow-xl max-w-xl mx-auto lg:mx-0 mb-8">
                <div className="flex flex-wrap gap-2 mb-3">
                  {(['phone', 'email', 'domain', 'ip'] as const).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleDemoTypeChange(type)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold capitalize transition-all ${
                        demoType === type
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {type === 'phone' ? '📱 Phone' : type === 'email' ? '📧 Email' : type === 'domain' ? '🌐 Domain' : '💻 IP Address'}
                    </button>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-2.5">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={demoQuery}
                      onChange={(e) => setDemoQuery(e.target.value)}
                      placeholder="Enter search target..."
                      className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-sm font-mono text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <span className="absolute right-3 top-3 text-xs text-emerald-500 font-semibold flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      Ready
                    </span>
                  </div>
                  <Button
                    onClick={handleGetStarted}
                    variant="primary"
                    size="md"
                    className="whitespace-nowrap rounded-xl shadow-md shadow-indigo-600/30"
                  >
                    Run Search ⚡
                  </Button>
                </div>

                <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                  <span>⚡ Instant API Fan-Out (45+ Engines)</span>
                  <span className="text-indigo-600 dark:text-indigo-400 font-medium">No credit card required to test</span>
                </div>
              </div>

              {/* Action Buttons & Proof */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-6">
                <Button
                  onClick={handleGetStarted}
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto px-8 py-3.5 text-base rounded-xl shadow-lg shadow-indigo-600/25"
                >
                  {user ? 'Enter Dashboard' : 'Start Investigating Free'}
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Button>
                {!user && (
                  <Link
                    to="/login"
                    className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-gray-300 dark:border-gray-700 font-semibold text-center text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Sign In to Account
                  </Link>
                )}
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 pt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  256-bit TLS Encrypted
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Zero Query History Logged
                </div>
                <div className="flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Gov & LEA Compatible
                </div>
              </div>
            </div>

            {/* Right Column: 3D Holographic Image Showcase */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* 3D Render Container with Perspective & Floating Animation */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-indigo-500/20 border border-slate-700/40 bg-slate-900 group">
                  <img
                    src="/images/3d/hero-3d.jpg"
                    alt="IntFinder 3D Holographic OSINT Intelligence Globe"
                    className="w-full h-auto aspect-video sm:aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="eager"
                  />
                  {/* Subtle dark gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-950/80 via-transparent to-transparent pointer-events-none" />

                  {/* Floating 3D Badge 1: Top Left */}
                  <div className="absolute top-4 left-4 glass-card px-3.5 py-2 rounded-xl border border-white/20 shadow-lg flex items-center gap-2.5 animate-float-3d">
                    <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping" />
                    <div>
                      <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-300">Data Network</div>
                      <div className="text-xs font-bold text-gray-900 dark:text-white">45+ Active OSINT APIs</div>
                    </div>
                  </div>

                  {/* Floating 3D Badge 2: Bottom Right */}
                  <div className="absolute bottom-4 right-4 glass-card px-4 py-2.5 rounded-xl border border-white/20 shadow-xl flex items-center gap-3 animate-float-reverse">
                    <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-base">
                      🛡️
                    </div>
                    <div>
                      <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-300">Biometric Shield</div>
                      <div className="text-xs font-bold text-emerald-500 dark:text-emerald-400">Zero Retention Active</div>
                    </div>
                  </div>
                </div>

                {/* Decorative neon back-light glow */}
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-indigo-500 to-cyan-500 opacity-20 blur-xl -z-10 group-hover:opacity-35 transition-opacity" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Banner */}
      <section className="py-12 bg-gradient-to-r from-indigo-900 via-indigo-950 to-purple-950 text-white relative overflow-hidden border-y border-indigo-800/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 text-center">
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-cyan-200 mb-1">
                {stats.totalSearches.toLocaleString()}+
              </div>
              <div className="text-sm font-semibold text-indigo-200">OSINT Queries</div>
              <div className="text-xs text-indigo-300/80 mt-0.5">Correlated Worldwide</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-indigo-200 mb-1">
                {stats.dataSources}+
              </div>
              <div className="text-sm font-semibold text-indigo-200">Integrated APIs</div>
              <div className="text-xs text-indigo-300/80 mt-0.5">Deep & Surface Web</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-200 mb-1">
                {stats.responseTime}
              </div>
              <div className="text-sm font-semibold text-indigo-200">Mean Latency</div>
              <div className="text-xs text-indigo-300/80 mt-0.5">Asynchronous Parallelism</div>
            </div>
            <div className="p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
              <div className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-emerald-200 mb-1">
                99.98%
              </div>
              <div className="text-sm font-semibold text-indigo-200">System Uptime</div>
              <div className="text-xs text-indigo-300/80 mt-0.5">High-Availability Cloud</div>
            </div>
          </div>
        </div>
      </section>

      {/* 3D Capabilities Deep-Dive Section */}
      <section id="capabilities" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="info" className="px-3 py-1 mb-3">
              Forensic Breadth
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Investigate Every Digital Footprint
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300">
              One query simultaneously branches across telecom registries, leak databases, breach dumps, domain registries, and social graphs.
            </p>
          </div>

          {/* 3D Feature Grid 1: Search Engine Architecture */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-20 bg-white dark:bg-gray-800/60 rounded-3xl p-6 sm:p-10 border border-gray-200/80 dark:border-gray-700 shadow-xl">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                Data Correlation
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1 mb-4">
                Multithreaded Deep Intelligence Engine
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Rather than logging into 20 separate forensic platforms, IntFinder dispatches concurrent queries to verified provider APIs. 
                Our content transformers normalize heterogeneous JSON responses into a clean, searchable report ready for export.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Breach & Credential Dumps</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Detect compromised emails and credentials.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Carrier & Telecom Records</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Identify line type, carrier origin & location.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">DNS & IP ASN Mapping</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Analyze infrastructure hosting & reverse DNS.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-sm">
                    ✓
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-white">Vehicle & Public Registries</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Instant validation across public registries.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-700/30 group">
                <img
                  src="/images/3d/search-3d.jpg"
                  alt="3D OSINT Search Engine Architecture"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute bottom-4 left-4 right-4 glass-card p-3 rounded-xl border border-white/20 text-xs">
                  <div className="font-semibold text-gray-900 dark:text-white">Real-Time Forensic Node Graph</div>
                  <div className="text-gray-500 dark:text-gray-300 text-[11px]">Normalized JSON output with high-confidence cross-referencing.</div>
                </div>
              </div>
            </div>
          </div>

          {/* 3D Feature Grid 2: Security & Privacy Vault */}
          <div id="security" className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-white dark:bg-gray-800/60 rounded-3xl p-6 sm:p-10 border border-gray-200/80 dark:border-gray-700 shadow-xl">
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-700/30 group">
                <img
                  src="/images/3d/shield-3d.jpg"
                  alt="3D Biometric Defense Shield & Encrypted Vault"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 right-4 glass-card px-3 py-1.5 rounded-lg border border-white/20 text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                  🔒 Zero-Knowledge Encrypted
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <span className="text-xs font-bold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">
                Confidentiality Standard
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1 mb-4">
                Enterprise Privacy & Law Enforcement Compliance
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Whether investigating cyber crime threats, vetting supplier risks, or responding to incidents, 
                your target parameters are processed in memory and never indexed or shared.
              </p>
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold text-xs">
                    ✓
                  </div>
                  <span>Strict zero-retention search parameters policy</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold text-xs">
                    ✓
                  </div>
                  <span>Official government & law enforcement credential verification</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold text-xs">
                    ✓
                  </div>
                  <span>Scoped API keys with per-IP rate control & role isolation</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 flex items-center justify-center font-bold text-xs">
                    ✓
                  </div>
                  <span>One-click tamper-evident PDF intelligence report exports</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50/70 dark:bg-gray-900/50 border-y border-gray-200/70 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white">
              Instant Intelligence in 3 Steps
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300">
              Designed for rapid response operations. Start searching in less than two minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 text-center glass-card-hover relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 text-white font-extrabold text-xl flex items-center justify-center mx-auto mb-6 shadow-md shadow-indigo-500/25">
                01
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Create Free Account</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Sign up with your email or Google Auth. No contracts or upfront credit card requirements.
              </p>
            </Card>

            <Card className="p-8 text-center glass-card-hover relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 text-white font-extrabold text-xl flex items-center justify-center mx-auto mb-6 shadow-md shadow-purple-500/25">
                02
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Add Int Coins</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Recharge credits as you go or choose a cost-effective bundle. Every coin translates directly to searches.
              </p>
            </Card>

            <Card className="p-8 text-center glass-card-hover relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 text-white font-extrabold text-xl flex items-center justify-center mx-auto mb-6 shadow-md shadow-cyan-500/25">
                03
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Extract & Report</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Query target identifiers and receive aggregated intelligence reports with immediate export capabilities.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <section id="pricing" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge variant="success" className="px-3 py-1 mb-3">
              Clear & Transparent
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white">
              Flexible Credits, Zero Lock-In
            </h2>
            <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-300">
              Only pay for the searches you initiate. Top up Int Coins anytime.
            </p>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner text="Fetching current plan pricing..." />
            </div>
          ) : subscriptionPlans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
              {subscriptionPlans.map((plan, index) => {
                const isFeatured = index === 1;
                return (
                  <Card
                    key={plan.id}
                    className={`flex flex-col p-8 rounded-2xl transition-all duration-300 relative ${
                      isFeatured
                        ? 'border-2 border-indigo-600 dark:border-indigo-500 shadow-2xl shadow-indigo-500/15 md:-translate-y-2'
                        : 'border border-gray-200/90 dark:border-gray-700 shadow-lg'
                    }`}
                  >
                    {isFeatured && (
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                        <span className="bg-indigo-600 text-white text-xs font-extrabold px-3.5 py-1 rounded-full uppercase tracking-wider shadow-md">
                          Most Popular
                        </span>
                      </div>
                    )}

                    <div className="mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                      {plan.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{plan.description}</p>
                      )}
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl sm:text-5xl font-extrabold text-indigo-600 dark:text-indigo-400">
                          ₹{plan.price}
                        </span>
                      </div>
                      <div className="mt-2 inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        🪙 {plan.intCoins.toLocaleString()} Int Coins included
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8 flex-1 text-sm text-gray-600 dark:text-gray-300">
                      {plan.features && plan.features.length > 0 ? (
                        plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2.5">
                            <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span>{feature}</span>
                          </li>
                        ))
                      ) : (
                        <>
                          <li className="flex items-center gap-2.5">
                            <span className="text-emerald-500">✓</span> Instant API query access
                          </li>
                          <li className="flex items-center gap-2.5">
                            <span className="text-emerald-500">✓</span> Full report history storage
                          </li>
                          <li className="flex items-center gap-2.5">
                            <span className="text-emerald-500">✓</span> PDF & JSON report exports
                          </li>
                        </>
                      )}
                      {plan.intCoinsExpirationDays && (
                        <li className="flex items-center gap-2.5 text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-100 dark:border-gray-800">
                          <span>⏱️ Validity: {plan.intCoinsExpirationDays} days</span>
                        </li>
                      )}
                    </ul>

                    <Button
                      onClick={handleGetStarted}
                      variant={isFeatured ? 'primary' : 'outline'}
                      className="w-full py-3 rounded-xl font-semibold"
                    >
                      Choose Plan
                    </Button>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 dark:text-gray-300">Flexible pricing plans are loading. You can still register and test!</p>
              <Button onClick={handleGetStarted} variant="primary" className="mt-4">
                Get Started Free
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Frequently Asked Questions */}
      <section className="py-20 bg-gray-50/70 dark:bg-gray-900/50 border-t border-gray-200/80 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Frequently Asked Questions</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Everything you need to know about our OSINT search architecture.</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = activeFaq === index;
              return (
                <div
                  key={index}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => setActiveFaq(isOpen ? null : index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center text-base font-semibold text-gray-900 dark:text-white focus:outline-none"
                  >
                    <span>{faq.q}</span>
                    <svg
                      className={`w-5 h-5 text-indigo-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-100 dark:border-gray-700/60 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pre-Footer Call to Action */}
      <section className="py-20 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-6">
            Empower Your Investigations Today
          </h2>
          <p className="text-lg sm:text-xl text-indigo-100 max-w-2xl mx-auto mb-8 leading-relaxed">
            Join thousands of cybersecurity analysts, fraud investigators, and researchers who rely on IntFinder for fast, reliable intelligence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGetStarted}
              className="bg-white text-indigo-700 hover:bg-indigo-50 px-8 py-4 rounded-xl text-base font-bold shadow-xl transition-all"
            >
              {user ? 'Open Dashboard' : 'Start Investigating Free'}
            </button>
            <Link
              to="/home/contact_us"
              className="bg-indigo-800/60 hover:bg-indigo-800 text-white border border-indigo-400/40 px-8 py-4 rounded-xl text-base font-semibold transition-all"
            >
              Contact Enterprise Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Responsive Footer */}
      <footer className="bg-gray-950 text-gray-400 py-16 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <ThemeAwareLogo size="md" showText={false} />
                <span className="text-2xl font-extrabold text-white">IntFinder</span>
              </div>
              <p className="text-sm text-gray-400 max-w-sm mb-4 leading-relaxed">
                Unified Open Source Intelligence search and correlation platform. Built for modern investigators, researchers, and security teams.
              </p>
              <div className="text-xs text-gray-500">
                A Product of TECKVINI SOLUTIONS PRIVATE LIMITED
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Platform</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/app/search" className="hover:text-white transition-colors">Search Terminal</Link></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Credit Pricing</a></li>
                <li><a href="#capabilities" className="hover:text-white transition-colors">API Data Feeds</a></li>
                <li><Link to="/app/api-keys" className="hover:text-white transition-colors">Developer APIs</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/home/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/home/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/home/refund" className="hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/home/shipping" className="hover:text-white transition-colors">Shipping & Delivery</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/home/contact_us" className="hover:text-white transition-colors">Contact Support</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Create Account</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800/80 pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-4">
            <div>
              &copy; {new Date().getFullYear()} TECKVINI SOLUTIONS PRIVATE LIMITED. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <span>Encrypted OSINT Infrastructure</span>
              <span>•</span>
              <span>ISO & LEA Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
